import { FC } from 'react';

import Expander from '@ingka/expander';
import SSRIcon from '@ingka/ssr-icon';
import checkmarkIcon from '@ingka/ssr-icon/paths/checkmark';
import { hasTranslation, t } from '@onlinepayments/payments-library';

import ContactInfo from '../../interfaces/ContactInfo';
import DeliveryService from '../../interfaces/DeliveryService';
import DeliveryTimeWindow from '../../interfaces/DeliveryTimeWindow';
import { getDeliveryDate, getDeliveryTimeWindow } from '../../utils/dateTimeUtils';
import { serviceIdToTransportMethodMap } from '../../utils/serviceIdToTransportMethodMap';
import { Address } from '../toolkit';

interface TimeWindowProps {
  isRangeOfDays: boolean;
  timeWindow: DeliveryTimeWindow;
  serviceItemNo: string;
}

const TimeWindow: FC<TimeWindowProps> = ({ isRangeOfDays, timeWindow, serviceItemNo }) => {
  const isParcel = serviceIdToTransportMethodMap[serviceItemNo] === 'Parcel';

  const getTitle = () => {
    if (isRangeOfDays) return t('order.delivery_between');
    if (isParcel) return t('order.estimated_delivery_date_header');
    return t('order.delivery_date_label');
  };

  return (
    <div className="mb-100" key={timeWindow.fromLocalDateTime}>
      <p className="mb-0 text-bold">{getTitle()}</p>

      <p className="mb-0" data-testid="delivery-date">
        {getDeliveryDate(timeWindow, isRangeOfDays)}
      </p>

      {isRangeOfDays && hasTranslation('order.range_of_days_info') && (
        <Expander
          open={false}
          targetId="range-of-days-more-info"
          labelOpen={t('order.hide_info')}
          labelClosed={t('order.show_info')}
        >
          <div className="mt-100" data-testid="range-of-days-more-info-text">
            {t('order.range_of_days_info')}
          </div>
        </Expander>
      )}

      {!isRangeOfDays && <p>{getDeliveryTimeWindow(timeWindow)}</p>}
    </div>
  );
};

interface IHomeDeliveryInfoProps {
  shippingContact: ContactInfo;
  deliveryService: DeliveryService;
}

const HomeDeliveryInfo: FC<IHomeDeliveryInfoProps> = ({
  shippingContact,
  deliveryService: { isRangeOfDays, deliveryTimeWindows, serviceItemNo },
}) => {
  return (
    <div>
      <div className="checkmark-heading">
        <div className="checkmark-circle">
          <SSRIcon paths={checkmarkIcon} />
        </div>
        <strong>{t('delivery_info_header')}</strong>
      </div>

      <div className="mb-100">
        <p className="mb-0 text-bold">{t('order.deliver_to_label')}</p>
        <Address {...shippingContact} />
      </div>

      {deliveryTimeWindows.map((timeWindow) => (
        <TimeWindow
          key={serviceItemNo}
          isRangeOfDays={isRangeOfDays}
          timeWindow={timeWindow}
          serviceItemNo={serviceItemNo}
        />
      ))}
    </div>
  );
};

export default HomeDeliveryInfo;
